/**
 * Color utility functions for sidebar theming
 * Used to generate pastel backgrounds from module colors
 */

/**
 * Convert hex color to HSL values
 */
export function hexToHSL(hex: string): { h: number; s: number; l: number } {
  // Remove # if present
  const cleanHex = hex.startsWith('#') ? hex.slice(1) : hex;

  const r = parseInt(cleanHex.slice(0, 2), 16) / 255;
  const g = parseInt(cleanHex.slice(2, 4), 16) / 255;
  const b = parseInt(cleanHex.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

/**
 * Saturation below which a module color counts as achromatic (grey). Such
 * modules opt out of hue-based theming entirely rather than picking up a
 * phantom tint from the near-arbitrary hue of a desaturated hex.
 */
export const ACHROMATIC_SATURATION_THRESHOLD = 15;

/**
 * Generate a pastel background color suitable for the secondary nav panel.
 * Handles grey/neutral colors specially to avoid unwanted color tints.
 *
 * @param hex - The module's primary color in hex format
 * @param isDark - Whether dark mode is active
 * @returns HSL color string
 */
export function getSecondaryPanelBg(hex: string, isDark = false): string {
  const { h, s } = hexToHSL(hex);

  // For greys (low saturation < 15%), use a neutral light grey
  // This prevents Admin module and similar from getting unwanted color tints
  if (s < ACHROMATIC_SATURATION_THRESHOLD) {
    return isDark ? 'hsl(0, 0%, 14%)' : 'hsl(0, 0%, 97%)';
  }

  // For colors, use a subtle tinted pastel. Saturation/lightness are held just
  // strong enough that the per-module shell tint is perceptible against the
  // white content card without reading as a colored panel.
  return isDark
    ? `hsl(${h}, 18%, 13%)`
    : `hsl(${h}, 32%, 94.5%)`;
}

/**
 * The module accent as a CSS color string, for the `style={{ … }}` call sites
 * that can't use a Tailwind class (icon `color`, `borderColor`, and the
 * `moduleColor` prop threaded into the app-bar widgets).
 *
 * Always prefer the Tailwind `module-accent` color in `className`. Reach for
 * these only where the value must be a CSS string. Never pass a raw module hex
 * to either — it has arbitrary lightness and goes unreadable in one theme.
 */
export const MODULE_ACCENT_COLOR = 'hsl(var(--accent-module))';

/** The module accent at `alpha` (0-1) — for tinted fills in `style={{ … }}`. */
export function moduleAccentAlpha(alpha: number): string {
  return `hsl(var(--accent-module) / ${alpha})`;
}

/**
 * Normalize a module's brand hex into the `--accent-module` token value: the
 * hue-carrying accent used for selection, indication and focus within the
 * current module's chrome.
 *
 * The module palette spans wildly different lightness/saturation (`#800020`
 * maroon at L=25% through `#0EA5E9` sky at L=48%), so the raw hex is never
 * used directly — only its *hue* survives. Saturation is clamped into a band
 * that stays legible without shouting, and lightness is pinned per theme so
 * every module's accent has identical contrast behaviour against that theme's
 * surfaces.
 *
 * @param hex - The module's primary color in hex format
 * @param isDark - Whether dark mode is active
 * @returns The `h s% l%` triplet for `--accent-module`, or `null` when the
 *          module is achromatic and should inherit the theme default.
 */
export function getModuleAccentHsl(hex: string, isDark = false): string | null {
  const { h, s } = hexToHSL(hex);

  if (s < ACHROMATIC_SATURATION_THRESHOLD) return null;

  const saturation = Math.min(Math.max(s, 45), 70);
  const lightness = isDark ? 66 : 38;

  return `${Math.round(h)} ${Math.round(saturation)}% ${lightness}%`;
}

/**
 * Hex colours this normalizer accepts. Mirrors the backend's `HEX_COLOR_RE`
 * (`shared/services/document_design_service.py`) and the `branding_color`
 * JSON-schema pattern, so a value the API would store is exactly a value the UI
 * can render — six digits, no 3-digit form, no alpha, no `rgb()`.
 */
const BRANDING_HEX_RE = /^#[0-9A-Fa-f]{6}$/;

/**
 * Saturation ceiling for a company accent. Companies pick their own brand hex,
 * so an unclamped one can arrive fully saturated and read as neon chrome beside
 * the app's muted palette.
 */
const COMPANY_ACCENT_MAX_SATURATION = 70;

/**
 * Lightness pinned per theme, so every company's accent has identical contrast
 * behaviour against that theme's surfaces regardless of how light or dark the
 * admin's chosen hex was.
 *
 * These are DELIBERATELY not the module accent's pins (38 / 66). The module
 * palette is a closed set of ~30 curated hexes with no yellow in it; a company
 * hex is admin-chosen and open, so the pin has to clear 3:1 for the worst hue
 * on the wheel. A fully saturated yellow at lightness 38% measures ~2.4:1 on a
 * white card — it would pass here only because nobody had tried it yet.
 * `companyAccentContrast.test.ts` sweeps the whole wheel and is the proof.
 */
const COMPANY_ACCENT_LIGHTNESS_LIGHT = 32;
const COMPANY_ACCENT_LIGHTNESS_DARK = 66;

/**
 * Normalize a company's `branding_color` into the `--accent-company` token: the
 * hue-carrying accent that marks which legal entity you are looking at.
 *
 * The stored hex is the company's real brand colour and is used AS-IS on
 * generated documents (the document-design cascade reads `branding_color`
 * directly). It is NOT used as-is in the UI, because the app renders on two
 * theme backgrounds and an arbitrary brand hex is legible on at most one of
 * them — Betty's near-black disappears in dark mode, a bright brand yellow
 * disappears in light. So the UI keeps the HUE, caps saturation, and pins
 * lightness per theme.
 *
 * Unlike {@link getModuleAccentHsl} this never returns `null` for an achromatic
 * brand: a module with a grey hex can fall back to the theme default because it
 * has a name and an icon elsewhere, but a company's accent IS the cue, and a
 * greyscale-branded company (Betty) still has to be told apart from the next
 * row. Achromatic input collapses to saturation 0 and keeps the pinned
 * lightness, which renders a visible neutral swatch rather than nothing.
 *
 * @param hex - The company's `branding_color`, `#RRGGBB`.
 * @param isDark - Whether dark mode is active.
 * @returns The `h s% l%` triplet for `--accent-company`, or `null` when the
 *          company has no colour set or the stored value is unparseable (the
 *          caller then renders its neutral fallback).
 */
export function getCompanyAccentHsl(
  hex: string | null | undefined,
  isDark = false,
): string | null {
  if (!hex || !BRANDING_HEX_RE.test(hex)) return null;

  const { h, s } = hexToHSL(hex);

  const saturation =
    s < ACHROMATIC_SATURATION_THRESHOLD ? 0 : Math.min(s, COMPANY_ACCENT_MAX_SATURATION);
  const lightness = isDark ? COMPANY_ACCENT_LIGHTNESS_DARK : COMPANY_ACCENT_LIGHTNESS_LIGHT;

  return `${Math.round(h)} ${Math.round(saturation)}% ${lightness}%`;
}

/**
 * Derive the chart series 2–5 ramp from a module's brand hue, so a multi-series
 * chart stays in the module's chromatic family instead of dropping the theme
 * after series 1 (series 1 is `--chart-1`, the accent itself).
 *
 * The four extra series rotate the base hue in tasteful, uneven steps — wide
 * enough that adjacent series read as distinct categories, anchored so the whole
 * set still feels related to the module. Saturation is held in the accent's
 * legible band and lightness pinned per theme (close to the accent's, nudged
 * darker in light mode — see the inline note), so every series clears the 3:1
 * non-text contrast bar on the card in both light and dark.
 *
 * @param hex - The module's primary color in hex format
 * @param isDark - Whether dark mode is active
 * @returns Four `h s% l%` triplets for `--chart-2`..`--chart-5`, or `null` when
 *          the module is achromatic and should keep the static globals.css ramp.
 */
export function getModuleChartRamp(
  hex: string,
  isDark = false,
): [string, string, string, string] | null {
  const { h, s } = hexToHSL(hex);

  if (s < ACHROMATIC_SATURATION_THRESHOLD) return null;

  // Same saturation band as the accent (getModuleAccentHsl); hue alone separates
  // the categories. Lightness is pinned per theme just slightly darker than the
  // accent in light mode (34 vs 38) so that even a rotated hue landing on the
  // perceptually-light yellow band still clears the 3:1 non-text contrast bar on
  // a white card. Dark mode reuses the accent's lightness (plenty of contrast on
  // the dark card). Enforced by moduleChartRamp.test.ts.
  const saturation = Math.min(Math.max(s, 45), 70);
  const lightness = isDark ? 66 : 34;
  // Uneven hue offsets from the accent: a near-analogous pair, then two further
  // around the wheel for clear categorical separation without hitting the exact
  // complement (which can read as a clashing accent rather than a sibling series).
  const HUE_OFFSETS = [40, 84, 132, 200] as const;

  return HUE_OFFSETS.map(
    (offset) => `${Math.round((h + offset) % 360)} ${saturation}% ${lightness}%`,
  ) as [string, string, string, string];
}

/**
 * Generate a pastel color from a hex color with custom lightness and saturation.
 * Useful for module theming options.
 */
export function generatePastel(hex: string, lightness: number, saturation: number): string {
  const { h, s } = hexToHSL(hex);
  // For greys (low saturation), keep saturation low to avoid color tint
  const effectiveSaturation = s < 15 ? Math.min(saturation, 5) : saturation;
  return `hsl(${h}, ${effectiveSaturation}%, ${lightness}%)`;
}
